import React, { useRef, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  FiberManualRecord as RecordIcon,
  Stop as StopIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  MediaRecorderState,
  ProgressModal,
  FeedbackSnackbar,
  uploadToScreenApp,
  stopMediaRecording,
  useIframeResizer,
} from './common';

const TabAudioRecorder: React.FC = () => {
  const { t } = useTranslation();
  useIframeResizer();

  const [state, setState] = useState<MediaRecorderState>({
    isRecording: false,
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
  });
  const [recordingTime, setRecordingTime] = useState('00:00');
  const [includeMicrophone, setIncludeMicrophone] = useState(true);

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const displayStream = useRef<MediaStream | null>(null);
  const audioStream = useRef<MediaStream | null>(null);
  const recordingBlob = useRef<Blob | null>(null);
  const startTimeRef = useRef<number>(0);
  const timerInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioContext = useRef<AudioContext | null>(null);
  const audioDestination = useRef<MediaStreamAudioDestinationNode | null>(null);

  const updateRecordingTime = () => {
    const elapsedTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const minutes = Math.floor(elapsedTime / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (elapsedTime % 60).toString().padStart(2, '0');
    setRecordingTime(`${minutes}:${seconds}`);
  };

  const stopRecording = useCallback(() => {
    if (!mediaRecorder.current || mediaRecorder.current.state !== 'recording')
      return;

    stopMediaRecording(mediaRecorder, [
      displayStream.current,
      audioStream.current,
    ]);

    // Clean up audio context
    if (audioContext.current?.state !== 'closed') {
      audioContext.current?.close();
    }

    if (timerInterval.current) {
      clearInterval(timerInterval.current);
    }

    setState((prev) => ({ ...prev, isRecording: false }));
  }, []);

  const startRecording = async () => {
    try {
      chunks.current = [];
      recordingBlob.current = null;

      // Get screen stream with audio
      displayStream.current = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      // Initialize audio context
      audioContext.current = new AudioContext();
      audioDestination.current = audioContext.current.createMediaStreamDestination();

      // Connect tab audio to the audio context
      const tabAudioSource = audioContext.current.createMediaStreamSource(
        displayStream.current
      );
      tabAudioSource.connect(audioDestination.current);

      if (includeMicrophone) {
        // Get microphone audio stream
        audioStream.current = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        // Connect microphone audio to the audio context
        const micAudioSource = audioContext.current.createMediaStreamSource(
          audioStream.current
        );
        micAudioSource.connect(audioDestination.current);
      }

      // Use the mixed audio stream for recording
      const combinedStream = audioDestination.current.stream;

      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000,
      };

      // Try to create with preferred codec, fallback to browser default if not supported
      if (MediaRecorder.isTypeSupported(options.mimeType)) {
        mediaRecorder.current = new MediaRecorder(combinedStream, options);
      } else {
        mediaRecorder.current = new MediaRecorder(combinedStream);
      }

      // Set up event handlers before starting
      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(chunks.current, {
          type: mediaRecorder.current?.mimeType || 'audio/webm;codecs=opus',
        });

        recordingBlob.current = audioBlob;
        setState((prev) => ({ ...prev, isRecording: false }));

        // Auto upload when recording stops
        uploadToScreenApp(
          audioBlob,
          'tab-audio-recording.webm',
          () => setState((prev) => ({ ...prev, uploading: true })),
          (progress) => setState((prev) => ({ ...prev, progress })),
          () => setState((prev) => ({ ...prev, uploadComplete: true })),
          (error) => setState((prev) => ({ ...prev, error }))
        );
      };

      // Cleanup function for audio context
      const cleanup = () => {
        if (audioContext.current?.state !== 'closed') {
          audioContext.current?.close();
        }
      };

      // Add cleanup to the existing stream end handler
      displayStream.current
        .getAudioTracks()[0]
        .addEventListener('ended', () => {
          stopRecording();
          cleanup();
        });

      // Start recording with a timeslice to get data periodically
      mediaRecorder.current.start(1000);
      setState((prev) => ({ ...prev, isRecording: true }));
      startTimeRef.current = Date.now();
      updateRecordingTime();
      timerInterval.current = setInterval(updateRecordingTime, 1000);
    } catch (error) {
      if (audioContext.current?.state !== 'closed') {
        audioContext.current?.close();
      }
      setState((prev) => ({
        ...prev,
        error: t('tabAudioRecorder.messages.error'),
      }));
    }
  };

  const handleCloseError = () => {
    setState((prev) => ({ ...prev, error: null }));
  };

  const handleReset = () => {
    // Reset all state to initial values
    setState({
      isRecording: false,
      uploading: false,
      progress: 0,
      uploadComplete: false,
      error: null,
    });
    setRecordingTime('00:00');
    setIncludeMicrophone(true);

    // Clear all refs
    recordingBlob.current = null;
    chunks.current = [];
    startTimeRef.current = 0;

    // Stop media recording and clear streams
    stopMediaRecording(mediaRecorder, [
      displayStream.current,
      audioStream.current,
    ]);
    displayStream.current = null;
    audioStream.current = null;

    // Clear intervals
    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }

    // Close audio context
    if (audioContext.current?.state !== 'closed') {
      audioContext.current?.close();
    }
    audioContext.current = null;
    audioDestination.current = null;
  };

  const handleSaveAgain = () => {
    if (recordingBlob.current) {
      uploadToScreenApp(
        recordingBlob.current,
        'tab-audio-recording.webm',
        () => setState((prev) => ({ ...prev, uploading: true, progress: 0 })),
        (progress) => setState((prev) => ({ ...prev, progress })),
        () => setState((prev) => ({ ...prev, uploadComplete: true })),
        (error) => setState((prev) => ({ ...prev, error }))
      );
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        textAlign: 'center',
      }}
    >
      {!state.isRecording && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            '& .MuiFormControlLabel-root': {
              margin: 0,
            },
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={includeMicrophone}
                onChange={(e) => setIncludeMicrophone(e.target.checked)}
                color="primary"
                size="small"
                sx={{
                  mx: 1,
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'primary.main',
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {includeMicrophone ? (
                  <MicIcon fontSize="small" sx={{ color: 'primary.main' }} />
                ) : (
                  <MicOffIcon
                    fontSize="small"
                    sx={{ color: 'text.disabled' }}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color: includeMicrophone
                      ? 'text.primary'
                      : 'text.secondary',
                    fontWeight: 400,
                    letterSpacing: '0.01em',
                  }}
                >
                  {t('tabAudioRecorder.controls.selectTab')}
                </Typography>
              </Box>
            }
          />
        </Box>
      )}
      {!state.isRecording && !recordingBlob.current && (
        <Button
          variant="contained"
          startIcon={<RecordIcon />}
          onClick={startRecording}
          size="large"
          color="error"
          sx={{
            minWidth: 140,
            px: 3,
            borderRadius: 28,
            py: 2,
            fontSize: '1.2rem',
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
            '&:hover': {
              backgroundColor: '#d32f2f',
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
            },
          }}
        >
          {t('tabAudioRecorder.controls.start')}
        </Button>
      )}
      {state.isRecording && (
        <Button
          variant="contained"
          startIcon={<StopIcon />}
          onClick={stopRecording}
          size="large"
          color="error"
          sx={{
            minWidth: 140,
            px: 3,
            borderRadius: 28,
            py: 2,
            fontSize: '1.2rem',
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
            '&:hover': {
              backgroundColor: '#d32f2f',
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
            },
          }}
        >
          {t('tabAudioRecorder.controls.stop')}
        </Button>
      )}
      {recordingBlob.current && !state.isRecording && (
        <Button
          variant="contained"
          startIcon={<RecordIcon />}
          onClick={startRecording}
          size="large"
          color="error"
          sx={{
            minWidth: 140,
            px: 3,
            borderRadius: 28,
            py: 2,
            fontSize: '1.2rem',
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
            '&:hover': {
              backgroundColor: '#d32f2f',
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
            },
          }}
        >
          {t('tabAudioRecorder.controls.start')}
        </Button>
      )}

      {state.isRecording && (
        <Typography
          sx={{
            fontSize: '1rem',
            color: 'text.secondary',
            mt: 2,
          }}
        >
          {recordingTime}
        </Typography>
      )}

      <ProgressModal
        open={state.uploading}
        progress={state.progress}
        isComplete={state.uploadComplete}
        processingMessage={t('tabAudioRecorder.messages.processing')}
        completeMessage={t('tabAudioRecorder.messages.complete')}
        onReset={handleReset}
        onSaveAgain={handleSaveAgain}
      />

      <FeedbackSnackbar
        open={!!state.error}
        message={state.error || t('tabAudioRecorder.messages.error')}
        severity="error"
        onClose={handleCloseError}
      />
    </Box>
  );
};

export default TabAudioRecorder;
